import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import React__default, { useMemo, useState } from 'react';
import Fade from 'embla-carousel-fade';
import useEmblaCarousel from 'embla-carousel-react';
import { carouselStyles } from './Carousel.styles.esm.js';
import { CarouselAutoplayToggle } from './CarouselAutoplayToggle.esm.js';
import { CarouselNavigation } from './CarouselNavigation.esm.js';
import { CarouselSlides } from './CarouselSlides.esm.js';
import { useCarouselAutoplay } from './hooks/useCarouselAutoplay.esm.js';
import { useCarouselNavigation } from './hooks/useCarouselNavigation.esm.js';
import { useCarouselSelection } from './hooks/useCarouselSelection.esm.js';
import { useFlexibleWidth } from './hooks/useFlexibleWidth.esm.js';
import { cn } from '../../utils/cn.esm.js';

function Carousel({ loop = false, itemsInView = 1, dragFree = false, animation = "scroll", autoAdvance = true, delay = 3, showFadeEdge = false, deviceType = "desktop", showItemPreview = true, hideCarouselButtonOnDisable = false, flexibleWidth = false, itemsToScroll = 1, itemGap = "md", children, className, inverse = false, carouselButtonVariant = "in-page", }) {
    const { root, fadeOutGradientLeft, fadeOutGradientRight, viewport, container, controls, buttons, } = carouselStyles({
        showFadeEdge,
        deviceType,
        animation,
        inverse,
    });
    // Convert itemGap token to pixel value
    const itemGapPx = itemGap === "md" ? 8 : 16;
    const carouselRef = React__default.useRef(null);
    const carouselContainerRef = React__default.useRef(null);
    // Use flexible width calculation when enabled (pre-calculate)
    const effectiveItemsInViewForOptions = flexibleWidth && animation !== "fade" ? itemsToScroll : itemsInView;
    const options = useMemo(() => (Object.assign({ loop, dragFree: animation === "fade" ? false : dragFree, duration: 30, slidesToScroll: animation === "fade" ? 1 : effectiveItemsInViewForOptions, align: "start" }, (effectiveItemsInViewForOptions > 1 && { containScroll: "trimSnaps" }))), [loop, dragFree, effectiveItemsInViewForOptions, animation]);
    const plugins = useMemo(() => {
        return animation === "fade" ? [Fade()] : [];
    }, [animation]);
    const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    // Track previous itemGap to detect changes
    const prevItemGapRef = React__default.useRef(itemGap);
    // Use flexible width calculation when enabled
    const { calculatedItemsInView } = useFlexibleWidth({
        containerRef: carouselContainerRef,
        emblaApi,
        flexibleWidth: flexibleWidth && animation !== "fade", // Only works with scroll animation
        itemsToScroll: itemsToScroll,
        itemGap,
        showItemPreview,
    });
    // Use calculated items in view when flexible width is enabled, otherwise use prop
    const effectiveItemsInView = flexibleWidth && animation !== "fade" ? calculatedItemsInView : itemsInView;
    useCarouselSelection(emblaApi, setSelectedIndex, setCanScrollPrev, setCanScrollNext);
    // Handle itemGap changes - recalculate scroll position to maintain alignment
    React__default.useEffect(() => {
        if (!emblaApi)
            return;
        const prevItemGap = prevItemGapRef.current;
        const currentItemGap = itemGap;
        // Only adjust if gap actually changed
        if (prevItemGap !== currentItemGap) {
            const currentScrollSnap = emblaApi.selectedScrollSnap();
            // Wait for DOM to update with new gap sizes, then re-initialize and snap
            const timeoutId = setTimeout(() => {
                // Force embla to recalculate slide positions and sizes
                emblaApi.reInit();
                // Then scroll to the same logical position
                emblaApi.scrollTo(currentScrollSnap, false); // false = no smooth scroll
            }, 50); // Increased timeout to ensure DOM updates complete
            prevItemGapRef.current = currentItemGap;
            return () => clearTimeout(timeoutId);
        }
    }, [emblaApi, itemGap]);
    const { scrollNext, scrollPrev, handleKeyDown } = useCarouselNavigation(emblaApi, carouselRef);
    const { isPlaying, toggleAutoAdvance } = useCarouselAutoplay(emblaApi, autoAdvance, delay, loop);
    // Convert children into slides
    const slides = useMemo(() => {
        const childrenArray = React__default.Children.toArray(children);
        return childrenArray.map((child, index) => ({
            id: index + 1,
            content: child,
        }));
    }, [children]);
    // Group slides for fade animation
    const slideGroups = useMemo(() => {
        const groups = [];
        for (let i = 0; i < slides.length; i += effectiveItemsInView) {
            groups.push(slides.slice(i, i + effectiveItemsInView));
        }
        return groups;
    }, [slides, effectiveItemsInView]);
    const remainingSlidesNext = useMemo(() => {
        if (!canScrollNext && !loop)
            return 0;
        const currentGroupIndex = (emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.selectedScrollSnap()) || 0;
        const currentItemIndex = currentGroupIndex * effectiveItemsInView;
        const remainingItems = slides.length - (currentItemIndex + effectiveItemsInView);
        if (loop) {
            return Math.max(1, remainingItems);
        }
        return Math.max(0, remainingItems);
    }, [emblaApi, loop, canScrollNext, effectiveItemsInView, slides.length]);
    const remainingSlidesPrev = useMemo(() => {
        if (!canScrollPrev && !loop)
            return 0;
        const currentGroupIndex = (emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.selectedScrollSnap()) || 0;
        const currentItemIndex = currentGroupIndex * effectiveItemsInView;
        const remainingItems = currentItemIndex;
        if (loop) {
            return Math.max(1, remainingItems);
        }
        return Math.max(0, remainingItems);
    }, [emblaApi, loop, canScrollPrev, effectiveItemsInView]);
    const isFirstSlide = selectedIndex === 0;
    const isLastSlide = selectedIndex === slideGroups.length - 1;
    const carouselAriaLabel = useMemo(() => {
        let label = "Carousel region. Use left and right arrow keys or buttons to navigate.";
        if (loop) {
            if (isFirstSlide) {
                label += " First item. Previous is last item.";
            }
            else if (isLastSlide) {
                label += " Last item. Next is first item.";
            }
        }
        return label;
    }, [loop, isFirstSlide, isLastSlide]);
    const carouselPositionLabel = useMemo(() => {
        if (effectiveItemsInView === 1) {
            return `Item ${selectedIndex + 1} of ${slides.length}.`;
        }
        return `Items ${selectedIndex * effectiveItemsInView + 1} to ${Math.min((selectedIndex + 1) * effectiveItemsInView, slides.length)} of ${slides.length}.`;
    }, [effectiveItemsInView, selectedIndex, slides.length]);
    return (jsxs(Fragment, { children: [jsxs("div", { "aria-roledescription": "carousel", tabIndex: 0, "aria-label": carouselAriaLabel, className: cn(root(), className), onKeyDownCapture: handleKeyDown, ref: carouselRef, children: [jsx("div", { "aria-live": "polite", className: "iris:sr-only", children: carouselPositionLabel }), jsx(CarouselAutoplayToggle, { autoAdvance: autoAdvance, isPlaying: isPlaying, onToggle: toggleAutoAdvance, inverse: inverse, itemsInView: effectiveItemsInView }), showFadeEdge && animation !== "fade" && (jsxs(Fragment, { children: [(canScrollPrev || loop) && (jsx("div", { className: fadeOutGradientLeft() })), (canScrollNext || loop) && (jsx("div", { className: fadeOutGradientRight() }))] })), jsx("div", { className: viewport(), ref: emblaRef, children: jsx("div", { className: container(), ref: carouselContainerRef, children: jsx(CarouselSlides, { animation: animation, slideGroups: slideGroups, slides: slides, itemsInView: effectiveItemsInView, deviceType: deviceType, loop: loop, emblaApi: emblaApi, selectedIndex: selectedIndex, showItemPreview: showItemPreview, flexibleWidth: flexibleWidth, itemsToScroll: itemsToScroll, itemGap: itemGapPx }) }) })] }), jsx("div", { className: cn(controls(), "navigation-controls"), children: jsx("div", { className: buttons(), children: jsx(CarouselNavigation, { deviceType: deviceType, itemsInView: effectiveItemsInView, canScrollPrev: canScrollPrev, canScrollNext: canScrollNext, loop: loop, scrollPrev: scrollPrev, scrollNext: scrollNext, inverse: inverse, remainingSlidesNext: remainingSlidesNext, remainingSlidesPrev: remainingSlidesPrev, hideCarouselButtonOnDisable: hideCarouselButtonOnDisable, carouselButtonVariant: carouselButtonVariant }) }) })] }));
}

export { Carousel };
